/**
 * Read-only deck viewer for phones, native, and any client without the
 * desktop-web slide editor.
 *
 * Renders `presentationSpec` directly — the spec already ships inside the file
 * payload, so nothing here parses the .pptx or hits the network.
 *
 * A 13.333" slide scaled to phone width makes 18pt body text about 7px, so a
 * faithful preview alone would be unreadable. The preview shows the design and
 * a readable outline below it shows the content, which is also what makes the
 * deck accessible to a screen reader.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text as RNText, View } from 'react-native';
import { Image } from 'expo-image';
import { SvgXml } from 'react-native-svg';
import { chartPreviewFromData } from '@/constants/utils/pptx_chart_preview';
import { presentationChartPreview } from '@/constants/utils/presentation_chart_preview';
import { getMacModalBackgroundView } from '@/lib/macModalBackgroundView';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import {
  bulletLines,
  elementBox,
  elementFontSize,
  elementTextColor,
  normalizeDeckForViewing,
  RAIL_LAYOUTS,
  displaySlideElements,
  shapeFill,
  slideOutline,
} from '@/constants/utils/presentation_spec';

const SLIDE_ASPECT_RATIO = 16 / 9;
const ACCENT_RAIL_PX_AT_REFERENCE = 8;
const REFERENCE_WIDTH = 960;

function NativeSlideChart({ data }) {
  const preview = useMemo(() => chartPreviewFromData(data), [data]);
  return preview ? <SvgXml xml={preview.svg} width="100%" height="100%" accessible accessibilityLabel={preview.alt} /> : null;
}

function StructuredSlideChart({ spec, theme }) {
  const preview = useMemo(() => presentationChartPreview(spec, theme), [spec, theme]);
  return preview ? <SvgXml xml={preview.svg} width="100%" height="100%" accessible accessibilityLabel={preview.alt} />
    : <RNText style={{ color: theme.text, padding: 12 }}>Chart preview unavailable</RNText>;
}

function SlideSurface({ slide, theme, width, slideSize, slideIndex = 0, preserveLayout = false }) {
  const accentRailWidth = Math.max(2, (width * ACCENT_RAIL_PX_AT_REFERENCE) / REFERENCE_WIDTH);

  return (
    <View
      style={{
        width,
        aspectRatio: slideSize ? slideSize.width / slideSize.height : SLIDE_ASPECT_RATIO,
        backgroundColor: theme.background,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {!preserveLayout && RAIL_LAYOUTS.has(slide?.layout) ? (
        <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: accentRailWidth, backgroundColor: theme.accent }} />
      ) : null}
      {displaySlideElements(slide, slideIndex, {
        snapImages: !preserveLayout,
        liftType: !preserveLayout,
      }).map((element, elementIndex) => {
        const box = elementBox(element, slideSize);
        const frame = {
          position: 'absolute',
          left: `${box.left}%`,
          top: `${box.top}%`,
          width: `${box.width}%`,
          height: `${box.height}%`,
          overflow: 'hidden',
        };

        if (element.type === 'shape') {
          return (
            <View
              key={element.id || `shape-${elementIndex}`}
              style={[frame, {
                backgroundColor: shapeFill(element, theme),
                borderRadius: element.radius ? 8 : 0,
                transform: Number.isFinite(element.rotation) ? [{ rotate: `${element.rotation}deg` }] : undefined,
              }]}
            />
          );
        }

        if (element.type === 'image') {
          if (!element.src) return null;
          return (
            <View key={element.id || `image-${elementIndex}`} style={frame}>
              {Platform.OS === 'macos' && element.chartData ? <NativeSlideChart data={element.chartData} /> : <Image
                source={{ uri: element.src }}
                accessibilityLabel={element.alt || undefined}
                style={{ width: '100%', height: '100%' }}
                contentFit={element.contentFit || 'cover'}
              />}
            </View>
          );
        }

        if (element.type === 'chart') {
          return <View key={element.id || `chart-${elementIndex}`} style={frame}>
            <StructuredSlideChart spec={element.spec} theme={theme} />
          </View>;
        }

        if (element.type === 'table') {
          const header = Array.isArray(element.header) ? element.header : [];
          const rows = [...(header.length ? [header] : []), ...(Array.isArray(element.rows) ? element.rows : [])];
          const columns = Math.max(1, ...rows.map(row => Array.isArray(row) ? row.length : 0));
          return (
            <View key={element.id || `table-${elementIndex}`} style={frame}>
              {rows.map((row, rowIndex) => (
                <View key={rowIndex} style={{ flexDirection: 'row', flex: element.rowHeights?.[rowIndex] || 1, minHeight: 0 }}>
                  {Array.from({ length: columns }, (_, column) => {
                    const original = element.cellStyles?.[rowIndex]?.[column] || {};
                    const isHeader = header.length > 0 && rowIndex === 0;
                    return (
                      <View key={column} style={{ flex: element.columnWidths?.[column] || 1, minWidth: 0,
                        justifyContent: 'center', overflow: 'hidden', paddingHorizontal: width * 4 / REFERENCE_WIDTH,
                        borderWidth: 0.5, borderColor: `${theme.accent}55`,
                        backgroundColor: original.background || (isHeader ? theme.accent : theme.background) }}>
                        <RNText style={{ fontSize: elementFontSize({ fontSize: original.fontSize || 18 }, width, slideSize),
                          color: original.color || (isHeader ? '#ffffff' : theme.text),
                          fontWeight: (original.bold ?? isHeader) ? '700' : '400', textAlign: original.align || 'left' }}>
                          {String(row?.[column] ?? '')}
                        </RNText>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          );
        }

        const fontSize = elementFontSize(element, width, slideSize);
        const body = element.type === 'bullets'
          ? bulletLines(element.items).join('\n')
          : String(element.text || '');
        if (!body) return null;

        return (
          <View key={element.id || `text-${elementIndex}`} style={[frame, {
            justifyContent: element.verticalAlign === 'center' ? 'center' : element.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
          }]}>
            <RNText
              style={{
                fontSize,
                fontFamily: /^[\p{L}\p{N} _-]{1,80}$/u.test(element.fontFace || '') ? element.fontFace : undefined,
                lineHeight: fontSize * (element.preserveTypography ? 1.12 : 1.18),
                color: elementTextColor(element, theme),
                fontWeight: element.bold ? '700' : '400',
                fontStyle: element.italic ? 'italic' : 'normal',
                textAlign: element.align || 'left',
              }}
            >
              {body}
            </RNText>
          </View>
        );
      })}
    </View>
  );
}

export default function SlideDeckViewer({ file, preserveLayout = false }) {
  const { colors } = useTheme();
  const [width, setWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [index, setIndex] = useState(0);
  const carouselRef = useRef(null);
  const indexRef = useRef(0);

  const deck = useMemo(() => normalizeDeckForViewing(
    file?.parameters?.presentationSpec,
    file?.parameters?.title || file?.name,
  ), [file]);
  const keepLayout = preserveLayout || file?.parameters?.presentationSpec?.preserveLayout === true;
  const slideSize = keepLayout ? deck.slideSize : undefined;
  const aspectRatio = slideSize ? slideSize.width / slideSize.height : SLIDE_ASPECT_RATIO;

  const goToSlide = useCallback((requested) => {
    const next = Math.max(0, Math.min(requested, deck.slides.length - 1));
    indexRef.current = next;
    setIndex(next);
    carouselRef.current?.scrollTo({ x: next * width, animated: false });
  }, [deck.slides.length, width]);

  useEffect(() => { goToSlide(indexRef.current); }, [goToSlide]);
  useEffect(() => {
    indexRef.current = 0;
    setIndex(0);
    carouselRef.current?.scrollTo({ x: 0, animated: false });
  }, [file]);

  const onKeyDown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    goToSlide(indexRef.current + (event.key === 'ArrowRight' ? 1 : -1));
  };

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1 },
    carousel: { paddingVertical: 16 },
    page: { paddingHorizontal: 16, alignItems: 'center' },
    counter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingBottom: 12 },
    navigationButton: {
      width: 44, height: 44, alignItems: 'center', justifyContent: 'center',
      borderRadius: 22, borderWidth: 1, borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_secondary, marginHorizontal: 12,
    },
    outline: {
      marginHorizontal: 16,
      marginBottom: 24,
      padding: 16,
      gap: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_secondary,
    },
    bulletRow: { flexDirection: 'row', gap: 8 },
    notes: {
      marginTop: 4,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
      gap: 4,
    },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 32 },
  }), [colors]);

  const onCarouselScroll = useCallback((event) => {
    if (!width) return;
    const next = Math.max(0, Math.min(deck.slides.length - 1, Math.round(event.nativeEvent.contentOffset.x / width)));
    indexRef.current = next;
    setIndex(previous => (previous === next ? previous : next));
  }, [width, deck.slides.length]);

  if (!deck.slides.length) {
    return (
      <View style={styles.empty}>
        <Icon name="file-powerpoint" size={30} color={colors.text_color_tertiary} />
        <Text color="secondary" style={{ textAlign: 'center' }}>This deck has no slides yet.</Text>
      </View>
    );
  }

  const activeIndex = Math.min(index, deck.slides.length - 1);
  // RN macOS does not implement accessibilityElementsHidden. This native
  // surface hides offscreen accessibility children without unmounting slides
  // needed for a smooth swipe between pages.
  const MacSlidePage = Platform.OS === 'macos' ? getMacModalBackgroundView() : null;
  const SlidePage = MacSlidePage || View;
  const outline = slideOutline(deck.slides[activeIndex]);
  // Leave room for navigation on a Mac window, including short, wide windows.
  const slideWidth = !width ? 0 : Platform.OS === 'macos'
    ? Math.min(width - 32, 1100, Math.max(180, viewportHeight - 180) * aspectRatio)
    : width - 32;

  return (
    <ScrollView style={styles.container} onLayout={event => {
      setWidth(event.nativeEvent.layout.width);
      setViewportHeight(event.nativeEvent.layout.height);
    }}
      {...(Platform.OS === 'web' ? { tabIndex: 0, onKeyDown, accessibilityLabel: 'Presentation slides. Use left and right arrow keys to browse.' } : {})}
    >
      {slideWidth > 0 ? (
        <>
          <ScrollView
            ref={carouselRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={onCarouselScroll}
            scrollEventThrottle={16}
            contentContainerStyle={styles.carousel}
          >
            {deck.slides.map((slide, slideIndex) => (
              <SlidePage key={slide.id} style={[styles.page, { width }]} collapsable={false}
                {...(MacSlidePage ? { accessibilityBlocked: slideIndex !== activeIndex } : {})}>
                <SlideSurface
                  slide={slide}
                  theme={deck.theme}
                  width={slideWidth}
                  slideSize={slideSize}
                  slideIndex={slideIndex}
                  preserveLayout={keepLayout}
                />
              </SlidePage>
            ))}
          </ScrollView>

          <View style={styles.counter}>
            {deck.slides.length > 1 ? (
              <Pressable accessibilityRole="button" accessibilityLabel="Previous slide"
                disabled={activeIndex === 0} accessibilityState={{ disabled: activeIndex === 0 }}
                onPress={() => goToSlide(indexRef.current - 1)}
                style={[styles.navigationButton, { opacity: activeIndex === 0 ? 0.35 : 1 }]}>
                <Icon name="chevron-left" size={18} color={colors.text_color_primary} />
              </Pressable>
            ) : null}
            <Text size="s" color="secondary" accessibilityLiveRegion="polite">
              {`Slide ${activeIndex + 1} of ${deck.slides.length}`}
            </Text>
            {deck.slides.length > 1 ? (
              <Pressable accessibilityRole="button" accessibilityLabel="Next slide"
                disabled={activeIndex === deck.slides.length - 1} accessibilityState={{ disabled: activeIndex === deck.slides.length - 1 }}
                onPress={() => goToSlide(indexRef.current + 1)}
                style={[styles.navigationButton, { opacity: activeIndex === deck.slides.length - 1 ? 0.35 : 1 }]}>
                <Icon name="chevron-right" size={18} color={colors.text_color_primary} />
              </Pressable>
            ) : null}
          </View>

          <View style={styles.outline}>
            {outline.title ? (
              <Text size="m" weight="semibold">{outline.title}</Text>
            ) : (
              <Text size="m" color="tertiary">Untitled slide</Text>
            )}
            {outline.body.map((line, lineIndex) => (
              <View key={`${activeIndex}-${lineIndex}`} style={styles.bulletRow}>
                <Text color="tertiary">•</Text>
                <Text style={{ flex: 1 }}>{line}</Text>
              </View>
            ))}
            {outline.imageCount > 0 ? (
              <Text size="s" color="tertiary">
                {outline.imageCount === 1 ? '1 image on this slide' : `${outline.imageCount} images on this slide`}
              </Text>
            ) : null}
            {outline.speakerNotes ? (
              <View style={styles.notes}>
                <Text size="s" weight="medium" color="secondary">Speaker notes</Text>
                <Text size="s" color="secondary">{outline.speakerNotes}</Text>
              </View>
            ) : null}
          </View>
        </>
      ) : null}
    </ScrollView>
  );
}
